import { AttachMoney, MoneyOff } from "@mui/icons-material";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import HistoryOutlinedIcon from "@mui/icons-material/HistoryOutlined";
import QrCode2Icon from "@mui/icons-material/QrCode2";
import * as MUI from "@mui/material";
import React from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import GoBack from "../../../../../components/buttons/GoBack";
import SimpleAlert from "../../../../../components/display/SimpleAlert";
import Loader from "../../../../../components/loader/Loader";
import PageContentWrapper from "../../../../../components/wrappers/PageContentWrapper";
import { GlobalDataContext } from "../../../../../contexts/GlobalData";
import {
  GET_WAREHOUSE_CATEGORIES,
  GET_WAREHOUSE_SUPPLIERS,
} from "../../../../../enums/ApiPaths";
import {
  DELETE_WAREHOUSE_ITEM,
  EDIT_WAREHOUSE_ITEM,
  VIEW_AUDITING,
} from "../../../../../enums/PermissionTitles";
import {
  WAREHOUSE_CATEGORIES,
  WAREHOUSE_SUPPLIERS,
} from "../../../../../enums/QueryKeys";
import {
  SINGLE_CLINIC_WAREHOUSE,
  SINGLE_CLINIC_WAREHOUSE_ID,
} from "../../../../../enums/UrlPaths";
import { useFetch } from "../../../../../hooks/Fetching";
import useUser from "../../../../../hooks/useUser";
import { getMasterLastBrowsedClinicId } from "../../../../../utils/masterBrowsingContext";
import DeleteWarehouseItemDialog from "./components/DeleteWarehouseItemDialog";
import WarehouseItemAuditingDialog from "./components/WarehouseItemAuditingDialog";
import WarehouseItemDetailsContent from "./components/WarehouseItemDetailsContent";
import WarehouseLabelBufferToggle from "./components/WarehouseLabelBufferToggle";
import WarehouseShoppingListToggle from "./components/WarehouseShoppingListToggle";
import WarehouseItemFormDialog from "./form/WarehouseItemFormDialog";
import { useGetWarehouseItem } from "./hooks/useGetWarehouseItem";
import { useGetWarehousePreferences } from "./hooks/useGetWarehousePreferences";

// Full-page view of a single warehouse item -- reachable directly by URL
// (e.g. from a QR code), not just by clicking through from the table. Same
// content and same actions (Edit/Delete/History) as
// WarehouseItemDetailsDialog, just in a dedicated page instead of a popup.
export default function WarehouseItemPage() {
  const { itemId } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { isPermissionAllowed } = React.useContext(GlobalDataContext);
  const { roles } = useUser();

  const { data: item, isLoading, error, refetch } = useGetWarehouseItem(itemId);

  const { data: warehousePreferences } = useGetWarehousePreferences(
    item?.clinicId,
  );
  const currency = warehousePreferences?.currency ?? "EUR";

  // Master has no clinic of their own, so "back" can't just go to the bare
  // clinic-less warehouse URL - it needs to return to the specific clinic
  // this item belongs to (item.clinicId), falling back to whichever clinic
  // master was last browsing if the item itself failed to load.
  const backUrl = roles.master
    ? SINGLE_CLINIC_WAREHOUSE_ID.replace(
        ":id",
        item?.clinicId ?? getMasterLastBrowsedClinicId() ?? "",
      )
    : SINGLE_CLINIC_WAREHOUSE;

  const isEditingItemsAllowed = isPermissionAllowed(EDIT_WAREHOUSE_ITEM);
  const isDeletingItemsAllowed = isPermissionAllowed(DELETE_WAREHOUSE_ITEM);
  const isViewingAuditingAllowed = isPermissionAllowed(VIEW_AUDITING);

  const [isFormOpen, setIsFormOpen] = React.useState(false);
  const [itemPendingDelete, setItemPendingDelete] = React.useState(null);
  const [isAuditingOpen, setIsAuditingOpen] = React.useState(false);

  const { data: categoriesData } = useFetch(
    GET_WAREHOUSE_CATEGORIES + item?.clinicId,
    null,
    [WAREHOUSE_CATEGORIES, item?.clinicId],
    Boolean(item?.clinicId),
  );
  const { data: suppliersData } = useFetch(
    GET_WAREHOUSE_SUPPLIERS + item?.clinicId,
    null,
    [WAREHOUSE_SUPPLIERS, item?.clinicId],
    Boolean(item?.clinicId),
  );

  const categoryNames = React.useMemo(
    () => Array.from(new Set((categoriesData ?? []).map((c) => c.name))).sort(),
    [categoriesData],
  );
  const supplierNames = React.useMemo(
    () => Array.from(new Set((suppliersData ?? []).map((s) => s.name))).sort(),
    [suppliersData],
  );

  if (isLoading) {
    return <Loader />;
  }

  if (error || !item) {
    return (
      <PageContentWrapper>
        <GoBack url={backUrl} title="ToWarehouseList" />
        <SimpleAlert message="WarehouseItemNotFound" severity="error" />
      </PageContentWrapper>
    );
  }

  return (
    <PageContentWrapper>
      <MUI.Stack direction="row" spacing={0.5} sx={{ mb: 1.5 }}>
        {isViewingAuditingAllowed && (
          <MUI.Tooltip title={t("History")}>
            <MUI.IconButton
              onClick={() => setIsAuditingOpen(true)}
              size="small"
            >
              <HistoryOutlinedIcon fontSize="small" />
            </MUI.IconButton>
          </MUI.Tooltip>
        )}
        {isEditingItemsAllowed && (
          <MUI.Tooltip title={t("Edit")}>
            <MUI.IconButton onClick={() => setIsFormOpen(true)} size="small">
              <EditIcon fontSize="small" />
            </MUI.IconButton>
          </MUI.Tooltip>
        )}
        {isDeletingItemsAllowed && (
          <MUI.Tooltip title={t("Delete")}>
            <MUI.IconButton
              onClick={() => setItemPendingDelete(item)}
              size="small"
              color="error"
            >
              <DeleteIcon fontSize="small" />
            </MUI.IconButton>
          </MUI.Tooltip>
        )}
        <WarehouseLabelBufferToggle item={item} />
        <WarehouseShoppingListToggle item={item} />
      </MUI.Stack>

      <MUI.Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 3 }}>
        <GoBack url={backUrl} title="ToWarehouseList" />
        <MUI.Stack
          direction="row"
          spacing={0.75}
          alignItems="center"
          sx={{ minWidth: 0 }}
        >
          <MUI.Typography variant="h5" sx={{ fontWeight: 700 }} noWrap>
            {item.title}
          </MUI.Typography>
          <MUI.Tooltip title={t("QrCodeAvailable")}>
            <QrCode2Icon fontSize="small" color="action" />
          </MUI.Tooltip>
          <MUI.Tooltip
            title={t(item?.sellable ? "ItemIsSellable" : "ItemNoSellable")}
          >
            {item?.sellable ? (
              <AttachMoney fontSize="small" color="success" />
            ) : (
              <MoneyOff fontSize="small" color="error" />
            )}
          </MUI.Tooltip>
        </MUI.Stack>
      </MUI.Stack>

      <WarehouseItemDetailsContent item={item} currency={currency} />

      {isFormOpen && (
        <WarehouseItemFormDialog
          open={isFormOpen}
          onClose={() => setIsFormOpen(false)}
          categories={categoryNames}
          suppliers={supplierNames}
          initialValues={{
            ...item,
            category: item.category?.name ?? "",
            supplier: item.supplier?.name ?? "",
          }}
          onSubmit={() => {
            setIsFormOpen(false);
            refetch();
          }}
        />
      )}

      <WarehouseItemAuditingDialog
        itemId={item.id}
        itemTitle={item.title}
        currency={currency}
        open={isAuditingOpen}
        onClose={() => setIsAuditingOpen(false)}
      />

      <DeleteWarehouseItemDialog
        item={itemPendingDelete}
        onCancel={() => setItemPendingDelete(null)}
        onDeleted={() => navigate(backUrl)}
      />
    </PageContentWrapper>
  );
}
